<template>
  <div data-component="app-launcher-root">
    <app-launcher-initializer
      :is-ready="isAuthReady"
      @sign-out="onSignOut"
    />

    <!-- TODO: Why events only work in camel case? -->
    <ExpirationOverlay
      data-component="expiration-overlay"
      :class="{ 'is-authorized': !cookiesExpired }"
      :is-opened="cookiesExpired"
      :lock-username="usernameLockScreen"
      :username="username"
      :auth-url="authUrl"
      :identity-provider="identityProvider"
      :token="reactiveToken"
      :user-id="userId"
      :account-id="accountId"
      :cookies-expired="cookiesExpired"
      :cookies-another-user="cookiesAnotherUser"
      :cookies-no-rights="cookiesNoRights"
      :login-overlay-disabled="false"
      @onUpdateCookieExpiration="onUpdateCookieExpirationEvent"
    />
    <component :is="ActionDeskWidget" />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, computed } from 'vue-demi';
import { ExpirationOverlay } from '@onereach/auth-ui-module';
import '@onereach/styles/main-v3.css';
import '@/styles/tokens.scss';

import AppLauncherInitializer from './AppLauncherInitializer.vue';
import { useOneReachConfig, useGlobalVars } from '@/composables';
import { useOneReachAuth } from './composables/common';

/** Root component that provides integration with OR Auth */
export default defineComponent({
  name: 'AppLauncher',
  components: {
    ExpirationOverlay,
    AppLauncherInitializer,
  },
  setup() {
    useGlobalVars().initializeVueRef();
    /* Providing configs is an initial step so all other modules can access variables */
    useOneReachConfig().initializeORConfig();

    const usernameLockScreen = ref('');
    const { getAPIInstance, getStore, getRefs } = useOneReachAuth();

    const {
      username,
      identityProvider,
      token,
      reactiveToken,
      authorized,
      cookiesAnotherUser,
      cookiesNoRights,
      cookiesExpired,
      userId,
      multiUserId,
      accountId,
    } = getRefs();
    const { init, updateCookieExpiration } = getStore();
    const { getORConfig } = useOneReachConfig();

    const ActionDeskWidget = ref(null as any);
    const isAuthReady = computed(() => {
      return authorized.value && token.value && !cookiesExpired.value || false;
    });

    const onAuthorizedEvent = async (isAuthorized: boolean | null) => {
      if (!isAuthorized) {
        return;
      }

      usernameLockScreen.value = await getAPIInstance().getUserNameForLockScreen({
        multiUserId: multiUserId.value || '',
        username: username.value || '',
        token: token.value,
      } as any);
    };

    const onUpdateCookieExpirationEvent = () => updateCookieExpiration();
    watch(authorized, onAuthorizedEvent);

    const onSignOut = () => {
      getAPIInstance().signOut(token.value);
      window.location.reload();
    };

    /**
     * When we changed user or account in another tab - reload page.
     * TODO: We can re-initialize app without page refresh if needed.
     */
    watch(cookiesAnotherUser, isAnotherUser => {
      if (isAnotherUser) {
        window.location.reload();
      }
    });

    return {
      accountId,
      authUrl: getORConfig().AUTH_UI_URL,
      cookiesAnotherUser,
      cookiesExpired,
      cookiesNoRights,
      identityProvider,
      isAuthorized: authorized,
      isAuthReady,
      usernameLockScreen,
      token,
      reactiveToken,
      username,
      userId,
      ActionDeskWidget,
      getORConfig,
      initializeAuthStore: init,
      onAuthorizedEvent,
      onUpdateCookieExpirationEvent,
      onSignOut,
    };
  },
  async created() {
    try {
      await this.initializeAuthStore({ initialPath: '', allowGuestLogin: true });
    } catch (e) {
      console.error('Error while initializing auth store!', e);
    }

    /* Redirect if not authorized */
    if (!this.isAuthorized) {
      const redirectToPath = encodeURIComponent(window.location.href);
      const path = `${this.getORConfig().AUTH_UI_URL}/login?redirectPath=${redirectToPath}`;
      window.location.replace(path);
    }

    const { CommonWidgetsProvider } = await import (
      /* webpackChunkName: "actiondesk" */
      '@onereach/actiondesk-widgets-dispatcher'
    );
    this.ActionDeskWidget = CommonWidgetsProvider;
  },
});
</script>

<style lang="scss" scoped>
[data-component='app-launcher-root'] {
  height: 100%;
  background: var(--or-c-surface-1);

  /* TODO: Investigate issue with style order? */
  ::v-deep .theme-border-outline:not(:checked) {
    border-style: solid !important;
  }
}

[data-component='expiration-overlay'] {
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  border: 0;
  /* Should be the highest z-index in the app */
  z-index: 1000;

  &.is-authorized {
    display: none;
  }
}
</style>
